import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Plus } from 'lucide-react'
import { getV1MeRetrieveQueryOptions, useTeacherAccountsList, useV1SchoolsList } from '@/api/client'
import { TeacherAccountsListStatus, V1SchoolsListState } from '@/api/model'
import type { TeacherAccountRow } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { AvatarPortrait } from '@/components/profile/dicebear-avatar'
import { avatarSeed } from '@/lib/avatar-catalog'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DataTable, type Column, type StateOption } from '@/components/data-table'
import { LookupTruncationNote } from '@/components/lookup-truncation-note'
import { TeacherForm } from '@/components/teachers/teacher-form'
import { queryHasHttpError } from '@/lib/query-state'
import { teacherStatusLabel, teacherStatusTone, teachersEmptyMessage } from '@/lib/teacher-labels'

type NauczycieleSearch = { school?: number }

export const Route = createFileRoute('/_panel/nauczyciele/')({
  // TR-702 R1: operators only — teachers are bounced like on every sibling
  // admin route (the deleted stub carried the same guard).
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role === 'teacher') {
      throw redirect({ to: '/' })
    }
  },
  // Accept `?school=<id>` so the school-detail "Zobacz nauczycieli" deep-link
  // lands the list pre-filtered to that school (TR-702 filter, TR-707).
  validateSearch: (search: Record<string, unknown>): NauczycieleSearch => {
    const school = Number(search.school)
    return Number.isFinite(school) && school > 0 ? { school } : {}
  },
  component: NauczycielePage,
})

const PAGE_SIZE = 25
const ALL_SCHOOLS = 'all'

const statusOptions: StateOption<TeacherAccountsListStatus>[] = [
  { value: TeacherAccountsListStatus.active, label: 'Aktywne' },
  { value: TeacherAccountsListStatus.archived, label: 'Zarchiwizowane' },
  { value: TeacherAccountsListStatus.all, label: 'Wszystkie' },
]

function NauczycielePage() {
  const navigate = useNavigate()
  const { school: schoolParam } = Route.useSearch()
  const [creating, setCreating] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<TeacherAccountsListStatus>(TeacherAccountsListStatus.active)
  const [school, setSchool] = useState<string>(schoolParam ? String(schoolParam) : ALL_SCHOOLS)
  const [ordering, setOrdering] = useState('last_name')

  const schoolsQuery = useV1SchoolsList({ state: V1SchoolsListState.active, page_size: 100 })
  const schools = schoolsQuery.data?.status === 200 ? schoolsQuery.data.data.results : []
  const schoolsCount = schoolsQuery.data?.status === 200 ? schoolsQuery.data.data.count : 0

  const query = useTeacherAccountsList({
    status,
    school: school === ALL_SCHOOLS ? undefined : Number(school),
    search: search || undefined,
    ordering: ordering || undefined,
    page,
    page_size: PAGE_SIZE,
  })
  const data = query.data?.status === 200 ? query.data.data : undefined

  if (creating) {
    return (
      <>
        <PageHeader
          title={
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" /> Nauczyciele
            </button>
          }
        />
        <div className="p-6">
          <h2 className="mb-6 text-lg font-semibold">Nowe konto nauczyciela</h2>
          <TeacherForm onClose={() => setCreating(false)} />
        </div>
      </>
    )
  }

  // TR-702: the row never shows the phone or the admin notes — details only.
  const columns: Column<TeacherAccountRow>[] = [
    {
      header: 'Nauczyciel',
      sortKey: 'last_name',
      cell: (t) => (
        <span className="flex items-center gap-2.5 font-medium">
          {/* alt pusty: nazwisko stoi obok. */}
          <AvatarPortrait options={t.avatar} seed={avatarSeed(t.email)} alt="" size="sm" />
          {t.last_name} {t.first_name}
        </span>
      ),
    },
    {
      header: 'E-mail',
      sortKey: 'email',
      cell: (t) => <span className="text-muted-foreground">{t.email}</span>,
    },
    {
      header: 'Tytuł / stanowisko',
      cell: (t) => <span className="text-muted-foreground">{t.title || '—'}</span>,
    },
    {
      header: 'Szkoły',
      sortKey: 'school_count',
      className: 'text-right',
      cell: (t) => <span className="tabular-nums text-muted-foreground">{t.school_count}</span>,
    },
    {
      header: 'Klasy',
      sortKey: 'class_count',
      className: 'text-right',
      cell: (t) => <span className="tabular-nums text-muted-foreground">{t.class_count}</span>,
    },
    {
      header: 'Status',
      sortKey: 'status',
      cell: (t) => (
        <StatusBadge tone={teacherStatusTone[t.status]}>{teacherStatusLabel[t.status]}</StatusBadge>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Nauczyciele"
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus /> Załóż konto nauczyciela
          </Button>
        }
      />
      <div className="p-6">
        <DataTable
          columns={columns}
          rows={data?.results ?? []}
          rowKey={(t) => t.id}
          count={data?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={PAGE_SIZE}
          search={search}
          onSearchChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          searchPlaceholder="Szukaj po imieniu, nazwisku lub e-mailu…"
          stateValue={status}
          stateOptions={statusOptions}
          onStateChange={(v) => {
            setStatus(v)
            setPage(1)
          }}
          ordering={ordering}
          onOrderingChange={(v) => {
            setOrdering(v)
            setPage(1)
          }}
          filters={
            <Select
              value={school}
              onValueChange={(v) => {
                setSchool(v)
                setPage(1)
              }}
            >
              <SelectTrigger size="sm" className="w-52">
                <SelectValue placeholder="Wszystkie szkoły" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SCHOOLS}>Wszystkie szkoły</SelectItem>
                {/* A ?school= deep link can point at a school outside the
                    active-100 lookup (e.g. archived) — surface it so the
                    applied filter is never invisible. */}
                {school !== ALL_SCHOOLS && !schools.some((s) => String(s.id) === school) && (
                  <SelectItem value={school}>Szkoła z filtra (#{school})</SelectItem>
                )}
                {schools.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </SelectItem>
                ))}
                <LookupTruncationNote count={schoolsCount} shown={schools.length} />
              </SelectContent>
            </Select>
          }
          isLoading={query.isLoading}
          isError={queryHasHttpError(query)}
          onRetry={() => void query.refetch()}
          emptyMessage={teachersEmptyMessage(
            search,
            status,
            school === ALL_SCHOOLS ? undefined : Number(school),
          )}
          onRowClick={(t) =>
            navigate({ to: '/nauczyciele/$nauczycielId', params: { nauczycielId: String(t.id) } })
          }
          countNoun={['konto', 'kont']}
        />
      </div>
    </>
  )
}
